import { Injectable } from '@angular/core';
import { ProductDisplayStep } from '@digilize/shared/definitions/src';
import { AppContext } from '@plys/src/application/app.context';
import { ProductsService } from '@plys/src/infrastructure/products.service';
import { Product, Supplement } from '@plys/src/models/product';
import { Show } from '@plys/src/models/show';
import { BehaviorSubject, filter, forkJoin, map, Observable, tap } from 'rxjs';

import { ShowsService } from '../../infrastructure/shows.service';

@Injectable({
  providedIn: 'root',
})
export class ShowState {
  private readonly _show = new BehaviorSubject<Show>(null);
  private readonly products = new BehaviorSubject(null);
  private readonly ticketPageSupplements = new BehaviorSubject(null);
  private readonly supplementPageSupplements = new BehaviorSubject(null);
  private readonly addons = new BehaviorSubject(null);

  constructor(
    private showsService: ShowsService,
    private productsService: ProductsService,
    private context: AppContext
  ) {}

  get showId(): string {
    return this._show.getValue().id;
  }

  get show$(): Observable<Show> {
    return this._show.asObservable();
  }

  get show(): Show {
    return this._show.value;
  }

  get products$(): Observable<Product[]> {
    return this.products.asObservable().pipe(filter((products) => !!products));
  }

  /** The show's products as already loaded, or null while the read is still in flight. */
  get loadedProducts(): Product[] | null {
    return this.products.value;
  }

  get ticketPageSupplements$(): Observable<Product[]> {
    return this.ticketPageSupplements.asObservable().pipe(filter((supplements) => !!supplements));
  }

  get suplementPageSupplements$(): Observable<Product[]> {
    return this.supplementPageSupplements.asObservable().pipe(filter((supplements) => !!supplements));
  }

  get addons$(): Observable<Product[]> {
    return this.addons.asObservable().pipe(filter((addons) => !!addons));
  }

  /** The POS's add-ons as already loaded, or null while the read is still in flight. */
  get loadedAddons(): Product[] | null {
    return this.addons.value;
  }

  get defaultProduct$(): Observable<Product> {
    return this.products$.pipe(map((products) => products[0]));
  }

  get showMenuRedirectUrl$(): Observable<string> {
    return this.show$.pipe(
      map((show) => show?.shopMenuUrl),
      filter((url) => !!url)
    );
  }

  /*   fetchProductsWithSupplements(
    products: Product[],
    displayStep: ProductDisplayStep
  ): Observable<{ product: Product; supplements: Supplement[] }[]> {
    return forkJoin(
      products?.map((product) =>
        this.fetchSupplements([product.id], displayStep).pipe(
          map((supplements) => ({
            product,
            supplements,
            supplements_ids: product.supplement_ids,
          }))
        )
      )
    );
  } */

  fetchShow(showId: string, posId: string): Observable<Show> {
    return forkJoin({
      products: this.fetchProducts(showId),
      show: this.showsService.getShow(showId, posId).pipe(
        map((response) => {
          return {
            id: response.id,
            title: response.movie_title,
            photo: {
              id: response.movie_photo?.id,
              url: response.movie_photo?.url1x || 'assets/sneak_preview.webp',
              title: response.movie_photo?.title || 'Sneak Preview',
              alt: response.movie_photo?.alt || 'Sneak Preview',
            },
            shopMenuUrl: response.shop_menu_url,
            cinemaName: response.shop_name,
            cinemaStreet: response.shop_street,
            areaName: response.area_name,
            areaId: response.area_id,
            shopId: response.shop_id,
            startTime: new Date(response.start),
            endTime: new Date(response.end),
            shopName: response.shop_name,
            disableAssignedSeating:
              !!response.disable_assigned_seating?.show || response.disable_assigned_seating?.area,
            is_cineville: response.is_cineville,
            pegis: response.movie_pegis ?? null,
          };
        }),
        tap((show) => this._show.next(show)),
        tap((show) => {
          document.title = `${show.shopName}`;
        })
      ),
    }).pipe(map(({ show }) => show));
  }

  fetchProducts(showId: string): Observable<Product[]> {
    const posId = this.context.posId;
    return this.productsService.getProductsByShow(showId, posId).pipe(tap((products) => this.products.next(products)));
  }

  fetchSupplements(productIds: string[], displayStep: ProductDisplayStep): Observable<Supplement[]> {
    return this.productsService.getSupplements(productIds, displayStep, this.showId).pipe(
      tap((items) => {
        if (displayStep === ProductDisplayStep.ProductsPage) {
          this.ticketPageSupplements.next(items);
        } else {
          this.supplementPageSupplements.next(items);
        }
      })
    );
  }

  fetchAddons(posId: string): Observable<Product[]> {
    return this.productsService.getAddonsByPosId(posId).pipe(tap((items) => this.addons.next(items)));
  }
}
